import React, { useState, useEffect, useRef } from 'react';
import { useCVContext } from '@/context/CVContext';
import { useLanguage } from '@/context/LanguageContext';
import { useGamification } from '@/context/GamificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, Briefcase, GraduationCap, 
  Code, Languages, Award, Target, TrendingUp, Star, Zap, Sparkles, ChevronRight,
  Plus, Users, Heart, HandHeart, BookOpen, Trash2
} from 'lucide-react';
import { celebrateMilestone, celebrateSection, getRandomTip } from '@/utils/celebrations';
import { LevelProgressBar, BadgesDisplay, StreakDisplay, ComparisonStats, AnimatedProgressCircle, RandomTip } from './GamificationUI';
import AddSectionModal from './AddSectionModal';
import { customSectionsApi } from '@/services/api';
import { toast } from 'sonner';

const SECTIONS_FR = [
  { key: 'identity', label: 'Identité', icon: User, weight: 10 },
  { key: 'contact', label: 'Contact', icon: Mail, weight: 10 },
  { key: 'bio', label: 'Bio', icon: User, weight: 10 },
  { key: 'target_job', label: 'Objectif', icon: Briefcase, weight: 5 },
  { key: 'education', label: 'Education', icon: GraduationCap, weight: 15 },
  { key: 'experience', label: 'Expériences', icon: Briefcase, weight: 20 },
  { key: 'skills', label: 'Compétences', icon: Code, weight: 10 },
  { key: 'certifications', label: 'Certifications', icon: Award, weight: 5 },
  { key: 'projects', label: 'Projets', icon: Code, weight: 5 },
  { key: 'languages', label: 'Langues', icon: Languages, weight: 5 },
  { key: 'awards', label: 'Récompenses', icon: Award, weight: 5 },
];

const SECTIONS_EN = [
  { key: 'identity', label: 'Identity', icon: User, weight: 10 },
  { key: 'contact', label: 'Contact', icon: Mail, weight: 10 },
  { key: 'bio', label: 'Bio', icon: User, weight: 10 },
  { key: 'target_job', label: 'Objective', icon: Briefcase, weight: 5 },
  { key: 'education', label: 'Education', icon: GraduationCap, weight: 15 },
  { key: 'experience', label: 'Experience', icon: Briefcase, weight: 20 },
  { key: 'skills', label: 'Skills', icon: Code, weight: 10 },
  { key: 'certifications', label: 'Certifications', icon: Award, weight: 5 },
  { key: 'projects', label: 'Projects', icon: Code, weight: 5 },
  { key: 'languages', label: 'Languages', icon: Languages, weight: 5 },
  { key: 'awards', label: 'Awards', icon: Award, weight: 5 },
];

// Calculate section quality (0-100)
const calculateSectionQuality = (profile, key) => {
  switch (key) {
    case 'identity':
      if (profile.first_name && profile.last_name) return 100;
      if (profile.first_name || profile.last_name) return 50;
      return 0;
      
    case 'contact':
      if (profile.contact?.email || profile.email) return 100;
      return 0;
    
    case 'bio':
      if (!profile.bio) return 0;
      const bioLength = profile.bio.length;
      if (bioLength > 150) return 100;
      if (bioLength > 80) return 75;
      if (bioLength > 30) return 50;
      return 25;
      
    case 'target_job':
      if (!profile.target_job) return 0;
      return profile.target_job.length > 5 ? 100 : 50;
      
    case 'education':
      if (!profile.education?.length) return 0;
      let eduScore = 0;
      profile.education.forEach(edu => {
        let itemScore = 0;
        if (edu.degree) itemScore += 40;
        if (edu.institution) itemScore += 40;
        if (edu.end_date) itemScore += 20;
        eduScore = Math.max(eduScore, itemScore);
      });
      return eduScore;
      
    case 'experience':
      if (!profile.experience?.length) return 0;
      let expScore = 0;
      profile.experience.forEach(exp => {
        let itemScore = 0;
        if (exp.position) itemScore += 25;
        if (exp.company) itemScore += 25;
        if (exp.description && exp.description.length > 30) itemScore += 30;
        if (exp.responsibilities?.length > 0) itemScore += 20;
        expScore = Math.max(expScore, itemScore);
      });
      return expScore;
      
    case 'skills':
      if (!profile.skills?.length) return 0;
      if (profile.skills.length >= 6) return 100;
      if (profile.skills.length >= 4) return 75;
      if (profile.skills.length >= 2) return 50;
      return 25;
      
    case 'certifications':
      if (!profile.certifications?.length) return 0;
      return profile.certifications.length >= 2 ? 100 : 70;
    
    case 'projects':
      if (!profile.projects?.length) return 0;
      return profile.projects.length >= 2 ? 100 : 70;
      
    case 'languages':
      if (!profile.languages?.length) return 0;
      let langScore = 0;
      profile.languages.forEach(lang => {
        if (lang.name && lang.level) langScore += 50;
        else if (lang.name) langScore += 25;
      });
      return Math.min(100, langScore);
      
    case 'awards':
      if (!profile.awards?.length) return 0;
      return profile.awards.length >= 1 ? 100 : 50;
      
    default:
      return 0;
  }
};

// Get color based on score
const getQualityColor = (score) => {
  if (score >= 75) return { bg: 'bg-[#5BA946]', text: 'text-[#5BA946]', light: 'bg-[#5BA946]/20' };
  if (score >= 40) return { bg: 'bg-orange-500', text: 'text-orange-600', light: 'bg-orange-100' };
  return { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-100' };
};

// Quality indicator component
const QualityIndicator = ({ score }) => {
  const color = getQualityColor(score);
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full ${color.bg}`} />
      <span className={`text-xs font-medium ${color.text}`}>{score}%</span>
    </div>
  );
};

// Get encouragement message based on completion
const getEncouragementMessage = (completion, language = 'fr') => {
  if (language === 'en') {
    if (completion >= 90) return { text: "Excellent! Your CV is almost complete.", icon: "🎉" };
    if (completion >= 75) return { text: "Very good! Just a few details left.", icon: "👍" };
    if (completion >= 50) return { text: "Good progress! Keep it up.", icon: "💪" };
    if (completion >= 25) return { text: "Good start!", icon: "🚀" };
    return { text: "Let's build your CV.", icon: "📝" };
  }
  if (completion >= 90) return { text: "Excellent ! Votre CV est presque complet.", icon: "🎉" };
  if (completion >= 75) return { text: "Très bien ! Plus que quelques détails.", icon: "👍" };
  if (completion >= 50) return { text: "Bon progrès ! Continuez ainsi.", icon: "💪" };
  if (completion >= 25) return { text: "C'est un bon début !", icon: "🚀" };
  return { text: "Commençons à construire votre CV.", icon: "📝" };
};

// Custom Sections Display Component
const CustomSectionsDisplay = ({ profile, language, onSectionDeleted }) => {
  const [deletingId, setDeletingId] = useState(null);

  const hasCustomContent = 
    (profile.references?.length > 0) ||
    (profile.interests?.length > 0) ||
    (profile.volunteering?.length > 0) ||
    (profile.publications?.length > 0) ||
    (profile.custom_sections?.length > 0);

  if (!hasCustomContent) return null;

  const handleDelete = async (type, id) => {
    if (!profile?.id) return;
    setDeletingId(`${type}-${id}`);
    
    try {
      switch (type) {
        case 'reference':
          await customSectionsApi.deleteReference(profile.id, id);
          toast.success(language === 'en' ? 'Reference deleted' : 'Référence supprimée');
          break;
        case 'volunteering':
          await customSectionsApi.deleteVolunteering(profile.id, id);
          toast.success(language === 'en' ? 'Volunteering deleted' : 'Bénévolat supprimé');
          break;
        case 'publication':
          await customSectionsApi.deletePublication(profile.id, id);
          toast.success(language === 'en' ? 'Publication deleted' : 'Publication supprimée');
          break;
        case 'custom':
          await customSectionsApi.deleteCustomSection(profile.id, id);
          toast.success(language === 'en' ? 'Section deleted' : 'Section supprimée');
          break;
        default:
          break;
      }
      onSectionDeleted?.();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error(language === 'en' ? 'Error deleting' : 'Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const isEn = language === 'en';

  return (
    <div className="pt-3 border-t border-gray-100 space-y-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {isEn ? 'Additional Sections' : 'Sections supplémentaires'}
      </p>

      {/* References */}
      {profile.references?.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Users className="w-4 h-4 text-blue-500" />
            {isEn ? 'References' : 'Références'} ({profile.references.length})
          </div>
          {profile.references.map((ref) => (
            <div key={ref.id} className="flex items-center justify-between py-1 px-2 bg-blue-50 rounded text-xs group">
              <span className="truncate">{ref.name}</span>
              <button
                onClick={() => handleDelete('reference', ref.id)}
                disabled={deletingId === `reference-${ref.id}`}
                className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Interests */}
      {profile.interests?.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Heart className="w-4 h-4 text-pink-500" />
            {isEn ? 'Interests' : 'Centres d\'intérêt'}
          </div>
          <div className="flex flex-wrap gap-1">
            {profile.interests.slice(0, 5).map((interest, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs py-0.5 px-2 bg-pink-50 text-pink-700">
                {interest}
              </Badge>
            ))}
            {profile.interests.length > 5 && (
              <Badge variant="outline" className="text-xs py-0.5 px-2">
                +{profile.interests.length - 5}
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Volunteering */}
      {profile.volunteering?.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <HandHeart className="w-4 h-4 text-green-500" />
            {isEn ? 'Volunteering' : 'Bénévolat'} ({profile.volunteering.length})
          </div>
          {profile.volunteering.map((vol) => (
            <div key={vol.id} className="flex items-center justify-between py-1 px-2 bg-green-50 rounded text-xs group">
              <span className="truncate">{vol.organization}</span>
              <button
                onClick={() => handleDelete('volunteering', vol.id)}
                disabled={deletingId === `volunteering-${vol.id}`}
                className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Publications */}
      {profile.publications?.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <BookOpen className="w-4 h-4 text-purple-500" />
            {isEn ? 'Publications' : 'Publications'} ({profile.publications.length})
          </div>
          {profile.publications.map((pub) => (
            <div key={pub.id} className="flex items-center justify-between py-1 px-2 bg-purple-50 rounded text-xs group">
              <span className="truncate">{pub.title}</span>
              <button
                onClick={() => handleDelete('publication', pub.id)}
                disabled={deletingId === `publication-${pub.id}`}
                className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Custom Sections */}
      {profile.custom_sections?.length > 0 && profile.custom_sections.map((section) => (
        <div key={section.id} className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <span>{section.icon || '📋'}</span>
              {section.title} ({section.items?.length || 0})
            </div>
            <button
              onClick={() => handleDelete('custom', section.id)}
              disabled={deletingId === `custom-${section.id}`}
              className="text-red-500 hover:text-red-700 p-1"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          {section.items?.slice(0, 2).map((item, idx) => (
            <div key={item.id || idx} className="py-1 px-2 bg-orange-50 rounded text-xs truncate">
              {item.title}
            </div>
          ))}
          {section.items?.length > 2 && (
            <p className="text-xs text-gray-400 pl-2">+{section.items.length - 2} {isEn ? 'more' : 'autres'}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default function ProfileSidebar({ onSectionClick }) {
  const { state, dispatch } = useCVContext();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const { profile, currentSection } = state;
  const [sectionQualities, setSectionQualities] = useState({});
  const [completion, setCompletion] = useState(0);
  const [tip, setTip] = useState(null);
  const [showTip, setShowTip] = useState(false);
  const prevCompletionRef = useRef(0);
  const [hasPersonalityResult, setHasPersonalityResult] = useState(false);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  
  // Check personality result on mount
  useEffect(() => {
    setHasPersonalityResult(!!localStorage.getItem('personalityResult'));
  }, []);
  
  // Gamification hooks
  const { checkBadges, incrementStreak, recordAction, points } = useGamification();
  
  // Get sections based on language
  const SECTIONS = language === 'en' ? SECTIONS_EN : SECTIONS_FR;

  // Serialize profile for dependency tracking
  const profileJson = JSON.stringify(profile);
  
  // Calculate qualities when profile changes
  useEffect(() => {
    if (profile) {
      const qualities = {};
      SECTIONS_FR.forEach(({ key }) => {
        qualities[key] = calculateSectionQuality(profile, key);
      });
      setSectionQualities(qualities);
      
      // Calculate completion immediately
      let totalScore = 0;
      let totalWeight = 0;
      SECTIONS_FR.forEach(({ key, weight }) => {
        const quality = qualities[key] || 0;
        totalScore += (quality / 100) * weight;
        totalWeight += weight;
      });
      const newCompletion = Math.round((totalScore / totalWeight) * 100);
      
      // Check for milestone achievements and celebrate
      const prevCompletion = prevCompletionRef.current;
      if (newCompletion > prevCompletion) {
        // Check milestones: 25%, 50%, 75%, 90%, 100%
        const milestones = [25, 50, 75, 90, 100];
        for (const milestone of milestones) {
          if (prevCompletion < milestone && newCompletion >= milestone) {
            celebrateMilestone(milestone);
            break;
          }
        }
        
        // Section completed celebration
        if (newCompletion - prevCompletion >= 5) {
          celebrateSection();
          incrementStreak();
        }
      }
      prevCompletionRef.current = newCompletion;
      setCompletion(newCompletion);
      
      // Check badges
      const stats = {
        completion: newCompletion,
        sectionsCompleted: Object.values(qualities).filter(q => q >= 50).length,
        experienceCount: profile.experience?.length || 0,
        educationCount: profile.education?.length || 0,
        skillCount: profile.skills?.length || 0,
        languageCount: profile.languages?.length || 0,
        certificationCount: profile.certifications?.length || 0,
        personalityCompleted: !!localStorage.getItem('personalityResult'),
        streak: parseInt(localStorage.getItem('gamification_streak') || '0', 10)
      };
      checkBadges(stats);
      
      console.log('ProfileSidebar: Profile updated, new completion:', newCompletion, qualities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileJson]);

  // Show random tip periodically
  useEffect(() => {
    const showRandomTip = () => {
      setTip(getRandomTip(language));
      setShowTip(true);
      setTimeout(() => setShowTip(false), 8000);
    };
    
    // Show tip after 30 seconds, then every 2 minutes
    const initialTimer = setTimeout(showRandomTip, 30000);
    const intervalTimer = setInterval(showRandomTip, 120000);
    
    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalTimer);
    };
  }, [language]);

  // Refresh profile from server after section is added/deleted
  const handleSectionAdded = async () => {
    if (!profile?.id) return;
    try {
      const { profileApi } = await import('@/services/api');
      const response = await profileApi.get(profile.id);
      if (response.data) {
        dispatch({ type: 'SET_PROFILE', payload: response.data });
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  };

  if (!profile) return null;

  const completionColor = getQualityColor(completion);
  const encouragement = getEncouragementMessage(completion, language);

  return (
    <Card className="sticky top-20" data-testid="profile-sidebar">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-bold">
            {profile.first_name?.[0] || '?'}{profile.last_name?.[0] || ''}
          </div>
          <div className="flex-1">
            <p className="font-semibold">
              {profile.first_name || (language === 'en' ? 'First name' : 'Prénom')} {profile.last_name || (language === 'en' ? 'Last name' : 'Nom')}
            </p>
            <p className="text-xs text-muted-foreground font-normal">
              {profile.target_job || (language === 'en' ? 'Objective not defined' : 'Objectif non défini')}
            </p>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-5 pt-2">
        {/* SOFT POWER CTA - Prominent position at top */}
        {!hasPersonalityResult ? (
          <div 
            className="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-xl p-4 shadow-lg cursor-pointer transform hover:scale-[1.02] transition-all duration-300"
            onClick={() => navigate('/personality')}
            data-testid="soft-power-cta"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="text-white font-bold text-sm">
                  {language === 'en' ? 'Discover your Soft Power!' : 'Découvrez votre Soft Power !'}
                </h4>
                <p className="text-white/80 text-xs mt-1 leading-relaxed">
                  {language === 'en' 
                    ? 'Complete the personality test to unlock your professional strengths'
                    : 'Complétez le test de personnalité pour débloquer vos forces professionnelles'
                  }
                </p>
              </div>
            </div>
            <Button 
              className="w-full mt-3 bg-white text-purple-600 hover:bg-white/90 font-semibold gap-2"
              size="sm"
            >
              {language === 'en' ? 'Start the test' : 'Commencer le test'}
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div 
            className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 shadow-lg cursor-pointer transform hover:scale-[1.02] transition-all duration-300"
            onClick={() => navigate('/personality-result')}
            data-testid="soft-power-completed-cta"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Star className="w-5 h-5 text-yellow-300" />
              </div>
              <div className="flex-1">
                <h4 className="text-white font-bold text-sm flex items-center gap-1">
                  {language === 'en' ? 'Soft Power Unlocked!' : 'Soft Power Débloqué !'}
                  <span className="text-yellow-300">✓</span>
                </h4>
                <p className="text-white/80 text-xs mt-0.5">
                  {language === 'en' ? 'View your personality report' : 'Voir votre rapport de personnalité'}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/70" />
            </div>
          </div>
        )}

        {/* Gamification Level & Points Display */}
        <div>
          <LevelProgressBar />
        </div>
        
        {/* Streak & Comparison Stats */}
        <div className="flex items-center justify-between gap-3 py-2 px-3 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-100">
          <StreakDisplay />
          <div className="w-px h-8 bg-gray-200"></div>
          <ComparisonStats />
        </div>

        {/* Contact Info */}
        <div className="space-y-2">
          {profile.contact?.email && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Mail className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{profile.contact.email}</span>
            </div>
          )}
          {profile.contact?.phone && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Phone className="w-4 h-4 flex-shrink-0" />
              <span>{profile.contact.phone}</span>
            </div>
          )}
          {profile.contact?.address && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="line-clamp-1">{profile.contact.address}</span>
            </div>
          )}
        </div>

        {/* Completion Progress with Encouragement */}
        <div className="pt-3 pb-2 border-t border-gray-100">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">{t('progression')}</span>
            </div>
            <span className={`text-lg font-bold ${completionColor.text}`}>{completion}%</span>
          </div>
          <Progress value={completion} className="h-3" />
          <div className="mt-3 space-y-1">
            <p className="text-xs text-gray-600 flex items-center gap-1.5">
              <span>{encouragement.icon}</span>
              <span>{encouragement.text}</span>
            </p>
            {completion < 90 && (
              <p className="text-xs text-gray-400">
                {language === 'en' ? 'Goal: 90% for a complete CV' : 'Objectif : 90% pour un CV complet'}
              </p>
            )}
          </div>
        </div>

        {/* Sections Status with Quality Scores */}
        <div className="pt-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            {t('qualityBySection')}
          </p>
          <div className="space-y-1.5">
            {SECTIONS.map(({ key, label, icon: Icon }) => {
              const quality = sectionQualities[key] || 0;
              const active = currentSection === key;
              const color = getQualityColor(quality);
              
              const isClickable = quality < 100 && onSectionClick;
              return (
                <div 
                  key={key}
                  onClick={isClickable ? () => onSectionClick(key, label) : undefined}
                  className={`flex items-center gap-3 py-2.5 px-3 rounded-lg transition-colors ${
                    active ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                  } ${isClickable ? 'cursor-pointer hover:bg-[#5BA946]/5 hover:border-[#5BA946]/20 border border-transparent' : ''}`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${quality > 0 ? color.text : 'text-gray-400'}`} />
                  <span className={`text-sm flex-1 truncate ${quality > 0 ? 'text-gray-900' : 'text-gray-500'}`}>
                    {label}
                  </span>
                  {quality > 0 ? (
                    <QualityIndicator score={quality} />
                  ) : (
                    <span className="text-xs text-gray-400 w-8 text-center">—</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Skills Preview */}
        {profile.skills?.length > 0 && (
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Compétences</p>
            <div className="flex flex-wrap gap-1.5">
              {profile.skills.slice(0, 5).map((skill, i) => (
                <Badge key={i} variant="secondary" className="text-xs py-1 px-2">
                  {skill}
                </Badge>
              ))}
              {profile.skills.length > 5 && (
                <Badge variant="outline" className="text-xs py-1 px-2">
                  +{profile.skills.length - 5}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Custom Sections Display */}
        <CustomSectionsDisplay 
          profile={profile} 
          language={language}
          onSectionDeleted={handleSectionAdded}
        />

        {/* Add Section Button */}
        <div className="pt-3 border-t border-gray-100">
          <Button
            onClick={() => setShowAddSectionModal(true)}
            variant="outline"
            className="w-full gap-2 border-dashed border-2 border-gray-300 hover:border-[#5BA946] hover:bg-green-50 text-gray-600 hover:text-[#5BA946]"
            data-testid="add-section-btn"
          >
            <Plus className="w-4 h-4" />
            {language === 'en' ? 'Add a section' : 'Ajouter une section'}
          </Button>
        </div>

        {/* Badges Section */}
        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
            <Star className="w-3.5 h-3.5" />
            {language === 'en' ? 'Badges & Goals' : 'Badges & Objectifs'}
          </p>
          <BadgesDisplay showLocked={true} showNextGoal={true} />
        </div>

        {/* Random Tip Display */}
        {showTip && tip && (
          <div className="pt-3 animate-slide-up">
            <RandomTip tip={tip} />
          </div>
        )}
      </CardContent>

      {/* Add Section Modal */}
      <AddSectionModal
        isOpen={showAddSectionModal}
        onClose={() => setShowAddSectionModal(false)}
        profileId={profile?.id}
        onSectionAdded={handleSectionAdded}
      />
    </Card>
  );
}
